import { fetch } from './globals'
import { commit, version } from './version'

export type JsonResponse<T = any> = {
  data: T | null
  error?: any
  /**
   * HTTP status of the response, when there was one. Callers that turn a
   * failed response into an AuthError need it — reading it back out of the
   * body only works for servers that happen to echo it there. `undefined`
   * means the request never completed (network failure) — there was no
   * server verdict at all.
   */
  status?: number
  /**
   * Stable machine code the server attached to a failed response
   * (`error_code` in the body, e.g. `user_suspended`). Unlike the
   * human-readable `error` message this is a contract — callers branch on
   * it to tell apart denials that share an HTTP status or OAuth code.
   */
  code?: string
}

type RequestInitWithToken = RequestInit & {
  token: string
  raw: boolean
}

const headers = (init: Partial<RequestInitWithToken> = {}) => {
  // Identify ourselves as a first-party client so the auth server can tell
  // auth-js (browser OAuth SDK) traffic apart from the dashboard, the
  // management SDK, or third-party integrations.
  //
  // Format: `<name>/<version>+<short-sha>` — semver build metadata, so the
  // commit rides along in the header the server ALREADY allows through CORS
  // and already parses. A separate header would have needed
  // `Access-Control-Allow-Headers` on the auth side deployed strictly before
  // this, and every request in between would have failed preflight.
  // Both segments are injected at release time (see ./version).
  let headers: Record<string, string> = {
    'x-faable-client': `auth-js/${version}+${commit}`
  }
  if (init?.token) {
    headers = { ...headers, Authorization: `Bearer ${init?.token}` }
  }
  return {
    ...init?.headers,
    ...headers
  }
}

/**
 * Human-readable reason out of an error body, across the two shapes this API
 * speaks.
 *
 * `/oauth/token` answers RFC 6749 §5.2: `{ error, error_description? }`, where
 * `error` is the OAuth code — or the message itself when the thrower set no
 * code. Everything else answers the http-errors shape:
 * `{ statusCode, error: 'Unauthorized', message }`.
 *
 * Reading `message` alone (as this did) meant the token endpoint's reason was
 * ALWAYS dropped: a rejected OTP arrives as `{"error":"Invalid or expired
 * OTP"}` and produced no error at all. `error` is read last on purpose — in the
 * http-errors shape it holds the status name, which is noise next to `message`.
 */
const _errorMessage = (body: unknown): string | undefined => {
  if (typeof body === 'string') return body || undefined
  if (!body || typeof body !== 'object') return undefined
  const b = body as Record<string, unknown>
  for (const key of ['error_description', 'message', 'error']) {
    const value = b[key]
    if (typeof value === 'string' && value) return value
  }
  return undefined
}

const _handleRes = async (
  res: Response,
  options: Partial<RequestInitWithToken> = {}
) => {
  const body = options.raw ? await res.text() : await res.json()
  if (res.status >= 300) {
    let parsed: unknown = body
    if (options.raw) {
      try {
        parsed = JSON.parse(body)
      } catch {
        parsed = body
      }
    }
    const parsed_code = (parsed as Record<string, unknown> | null)?.error_code
    return {
      data: body,
      // Never let a failed response come back with a falsy error: callers
      // branch on it, and an empty one sends them down the success path.
      error:
        _errorMessage(parsed) ?? `Request failed with status ${res.status}`,
      status: res.status,
      ...(typeof parsed_code === 'string' ? { code: parsed_code } : {})
    }
  }
  return { data: body, error: null, status: res.status }
}

export const _post = async <T>(
  url: string,
  data: object,
  options: Partial<RequestInitWithToken> = {}
): Promise<JsonResponse<T>> => {
  try {
    const res = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { ...headers(options), 'Content-Type': 'application/json' }
    })

    return await _handleRes(res, options)
  } catch (e) {
    return { data: null, error: e }
  }
}

export const _get = async <T>(
  url: string,
  options: Partial<RequestInitWithToken> = {}
): Promise<JsonResponse<T>> => {
  try {
    const res = await fetch(url, {
      ...options,
      method: 'GET',
      headers: headers(options)
    })

    return await _handleRes(res, options)
  } catch (e) {
    return { data: null, error: e }
  }
}

export const _patch = async <T>(
  url: string,
  data: object,
  options: Partial<RequestInitWithToken> = {}
): Promise<JsonResponse<T>> => {
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: { ...headers(options), 'Content-Type': 'application/json' }
    })

    return await _handleRes(res, options)
  } catch (e) {
    return { data: null, error: e }
  }
}

// DELETE. A 204 has no body: `_handleRes` would fail on `res.json()`, and an
// empty success is still a success.
export const _delete = async <T>(
  url: string,
  options: Partial<RequestInitWithToken> = {}
): Promise<JsonResponse<T | null>> => {
  try {
    const res = await fetch(url, {
      method: 'DELETE',
      headers: headers(options)
    })
    if (res.status === 204) return { data: null, error: null, status: 204 }
    return await _handleRes(res, options)
  } catch (e) {
    return { data: null, error: e }
  }
}
